/**
 * @fileoverview Utilities for transforming Zod validation errors to Toolpad
 * format for form validation.
 */

import { z } from 'zod';
import { ValidationIssue, ValidationResult } from './types';

/**
 * Validator function type that validates form values against a schema.
 * Accepts Partial<T> to handle incomplete form data during validation.
 */
export type ZodValidator<T> = (formValues: Partial<T>) => ValidationResult;

/**
 * Creates a validator function from a Zod schema that returns validation issues
 * in the Toolpad-compatible format.
 *
 * @template T - The expected type of valid data
 * @param schema - Zod schema to validate against
 * @returns Validator function that returns ValidationResult with typed issues
 */
export function createZodValidator<T>(schema: z.ZodSchema<T>): ZodValidator<T> {
  return (formValues: Partial<T>): ValidationResult => {
    const result = schema.safeParse(formValues);

    if (!result.success) {
      const issues: ValidationIssue[] = result.error.issues.map((issue) => ({
        message: issue.message,
        path: issue.path as (string | number)[],
      }));
      return { issues };
    }

    return { issues: [] };
  };
}
